<template>
  <div>
    <h2 class="font-20 font-weight-bold black2-color">
      {{ applicantTitle }}
    </h2>
    <BaseLabel text="Phone number" class="mt-3 mb-2" />
    <BaseInput
      v-model="questions.telNo.Value"
      :vid="questions.telNo.InterfaceKey"
      :data-uiid="questions.telNo.InterfaceKey"
      :hide-details="false"
      :rules="applicantPhoneNumberRules"
      :style="{
        'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
      }"
      :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
    />

    <BaseLabel text="Email address" class="mt-3 mb-2" />
    <BaseInput
      v-model="questions.emailAddress.Value"
      :vid="questions.emailAddress.InterfaceKey"
      :data-uiid="questions.emailAddress.InterfaceKey"
      :hide-details="false"
      :rules="applicantEmailAddressRules"
      :style="{
        'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
      }"
      :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
    />

    <BaseLabel text="Employment status" class="mt-3 mb-2" />
    <BaseSelect
      dense
      v-model="questions.employmentStatus.Value"
      :key="questions.employmentStatus.Value"
      :items="employmentStatusItems"
      :data-uiid="questions.employmentStatus.InterfaceKey"
      :hide-details="false"
      :selectedDefault="LABEL_CONSTANTS.SELECT_DEFAULT_PLACEHOLDER"
      :rules="{
        required: {
          required: true,
          fieldLabel: 'Employment status',
        },
      }"
      :style="{
        'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
      }"
      :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
    />

    <BaseLabel
      class="mt-3 mb-2"
      :helpText="$vuetify.breakpoint.lgAndUp ? occupationHelpText : ''"
      text="Occupation"
    />
    <BaseAutocomplete
      vid="Applicant1JobRole"
      className="custom-placeholder"
      v-model="questions.jobRole.Value"
      :data-uiid="questions.jobRole.InterfaceKey"
      :style="{
        'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
      }"
      :items="allOccupations"
      :hide-details="false"
      itemText="text"
      itemValue="value"
      placeholder="Start typing…"
      :noDataMatchingMessage="LABEL_CONSTANTS.NO_MATCHING_DATA_OCCUPATION"
      :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
      :rules="{
        required: {
          required: true,
          fieldLabel: 'Occupation',
        },
      }"
    />
    <BaseLabel text="Employer name" class="mt-3 mb-2" />
    <BaseInput
      v-model="questions.employerName.Value"
      :vid="questions.employerName.InterfaceKey"
      :data-uiid="questions.employerName.InterfaceKey"
      :hide-details="false"
      :rules="applicantEmployerNameRules"
      :style="{
        'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
      }"
      :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
    />

    <BaseLabel text="Length of service (months)" class="mt-3 mb-2" />
    <BaseInput
      v-model="questions.employerLengthOfService.Value"
      :vid="questions.employerLengthOfService.InterfaceKey"
      :data-uiid="questions.employerLengthOfService.InterfaceKey"
      :hide-details="false"
      type="number"
      :pattern="'\\d*'"
      :inputmode="'numeric'"
      :rules="applicantLengthOfServiceRules"
      :style="{
        'max-width': $vuetify.breakpoint.lgAndUp ? '132px' : '100%',
      }"
      :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import * as LABEL_CONSTANTS from "@/constants/label.constants";
import * as ERROR_MESSAGES from "@/constants/message.constants.js";

import { APPLICATION_MODULE } from "@/store/types/module-types";
import { EMAIL_REGEX, NAME_REGEX } from "@/constants/regex-patterns.constants";
import BaseInput from "@/components/BaseComponents/BaseInput.vue";
import BaseLabel from "@/components/BaseComponents/BaseLabel.vue";
import BaseSelect from "@/components/BaseComponents/BaseSelect";
import BaseAutocomplete from "@/components/BaseComponents/BaseAutocomplete";
import { ERROR_NAME_INVALID } from "@/constants/message.constants";
import { addFieldLabel } from "@/common/utils";
import {
  EMPLOYMENT_STATUS,
  JOB_ROLE,
  setItemDataUiids,
} from "@/constants/component.constants.js";

export default {
  components: {
    BaseLabel,
    BaseSelect,
    BaseInput,
    BaseAutocomplete,
  },
  props: {
    applicantTitle: { type: String, default: "Applicant" },
    occupationHelpText: {
      type: String,
      default: LABEL_CONSTANTS.OCCUPATION_HINT_HELP_TEXT,
    },
    questions: { type: Object, required: true },
  },
  data() {
    return {
      LABEL_CONSTANTS,
      ERROR_MESSAGES,
      ERROR_NAME_INVALID,
      applicantPhoneNumberRules: addFieldLabel(
        {
          required: {
            required: true,
          },
          phone_number: {},
        },
        "Phone number"
      ),
      applicantEmployerNameRules: addFieldLabel(
        {
          required: {
            required: true,
          },
          max: {
            length: 30,
            errorMessageTemplate: ERROR_MESSAGES.ERROR_MSG_MAX_CHARACTER,
          },
          regex: {
            pattern: NAME_REGEX,
            customMessage:
              "Only letters, spaces, hyphens, and apostrophes are allowed in this field.",
          },
        },
        "Employer name"
      ),
      applicantLengthOfServiceRules: addFieldLabel(
        {
          required: {
            required: true,
          },
        },
        "Length of service (months)"
      ),
      applicantEmailAddressRules: addFieldLabel(
        {
          required: {
            required: true,
          },
          max: {
            length: 50,
            errorMessageTemplate: ERROR_MESSAGES.ERROR_MSG_MAX_CHARACTER,
          },
          regex: {
            pattern: EMAIL_REGEX,
            customMessage: ERROR_MESSAGES.ERROR_INVALID_EMAIL_ADDRESS,
          },
        },
        "Email address"
      ),
    };
  },
  computed: {
    ...mapState(APPLICATION_MODULE, {
      applicationQuestionMap: "questionMap",
    }),

    employmentStatusItems() {
      return setItemDataUiids("EmploymentStatus", EMPLOYMENT_STATUS);
    },

    allOccupations() {
      return setItemDataUiids("Job", JOB_ROLE);
    },
  },
  watch: {
    "questions.employmentStatus.Value"(value) {
      if (["ShareholdingDirector", "Proprietor"].includes(value)) {
        this.$nextTick(() => {
          this.questions.employmentStatus.Value = "SelfEmployed";
        });
      } else {
        this.questions.employmentStatus.Value = value;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.custom-placeholder) {
  input {
    &::placeholder {
      font-style: italic;
    }
  }
}
</style>
